import { useCallback, useEffect, useState } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';

import { fg } from '@confluence/feature-gating';
import {
	useGetOnboardingState,
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useObjectSidebarState } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState';
import { useIsEstablishedUser } from '@confluence/onboarding-helpers/entry-points/hooks/useIsEstablishedUser';
import {
	LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS,
	AIFC_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useIsAIFCOnbardingEligible } from '@confluence/onboarding-ai-first-create/entry-points/useIsAIFCOnbardingEligible';

import { useIsLivePagesAvailableInSpace } from './useIsLivePagesAvailableInSpace';
import { useIsNav4Changeboarding } from './useIsNav4Changeboarding';

type LiveDocChangeboarding = Record<
	'introSpotlight' | 'liveDocTour' | 'contentWrapperTour' | 'aiButtonTour',
	{
		isVisible: boolean;
		complete: () => void;
	}
> & {
	error?: ApolloErrorInterop;
	loading?: true;
};

export const noChangeboarding: LiveDocChangeboarding = {
	introSpotlight: {
		isVisible: false,
		complete: () => {},
	},
	liveDocTour: {
		isVisible: false,
		complete: () => {},
	},
	contentWrapperTour: {
		isVisible: false,
		complete: () => {},
	},
	aiButtonTour: {
		isVisible: false,
		complete: () => {},
	},
};

const IS_ON_SPACE_OVERVIEW_ROUTES_ARGS = {
	selector: (routeName: string | undefined) => routeName === SPACE_OVERVIEW.name,
};

export const isLiveDocChangeboardingActive = (changeboarding: LiveDocChangeboarding): boolean =>
	Object.values(changeboarding).some((tour) => {
		if (typeof tour === 'object' && tour !== null && 'isVisible' in tour) {
			return tour.isVisible;
		}
		return false;
	});

export const useLiveDocChangeboarding: () => LiveDocChangeboarding = () => {
	const [introSpotlightDismissed, setIntroSpotlightDismissed] = useState(false);
	const { setOnboardingState } = useSetOnboardingState();
	const {
		isAIFCOnbardingEligible,
		loading: isAIFCOnbardingEligibleLoading,
		error: isAIFCOnbardingEligibleError,
	} = useIsAIFCOnbardingEligible();

	const { isAdminHubAIEnabled } = useSessionData();
	const {
		isEstablishedUser,
		loading: isEstablishedUserLoading,
		error: isEstablishedUserError,
	} = useIsEstablishedUser({
		skip:
			!fg('cc_onboarding_changeboarding_for_live_pages_eap') &&
			!fg('confluence_ai_uber_button_changeboarding'),
	});

	const { data, loading, error } = useGetOnboardingState(
		[
			...Object.values(LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS),
			...Object.values(AIFC_ONBOARDING_STATE_KEYS),
		],
		!fg('cc_modernize_ga_changeboarding') &&
			!fg('cc_onboarding_changeboarding_for_live_pages_eap') &&
			!fg('confluence_ai_uber_button_changeboarding'),
	);

	const {
		hasDismissedIntroSpotlight,
		hasDismissedLiveDocTour,
		hasDismissedAiButtonTour,
		hasSeenAIFCSpotlight,
		isAIFCSpotlightActive,
	} = deserializeState(data);
	const { isLicensed, isGuest } = useAccessStatus();

	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();
	const isLivePagesAvailableInSpace = useIsLivePagesAvailableInSpace();
	const isLivePage = useIsCurrentPageLive();
	const { sidebarControlState } = useObjectSidebarState();
	const isObjectSidebarShown = sidebarControlState === 'shown';
	const isOnSpaceOverviewRoute = useRouteName(IS_ON_SPACE_OVERVIEW_ROUTES_ARGS);
	const isAiButtonVisible = isObjectSidebarShown && isAdminHubAIEnabled;

	// AIFC onboarding is showing if the user is eligible and has not seen the spotlight or the spotlight is active
	const isAIFCOnboardingShowing =
		isAIFCOnbardingEligible && (!hasSeenAIFCSpotlight || isAIFCSpotlightActive);

	const introSpotlightBaseCase =
		!isAIFCOnboardingShowing &&
		!introSpotlightDismissed &&
		isLivePagesFeatureEnabled &&
		!hasDismissedLiveDocTour &&
		!hasDismissedIntroSpotlight &&
		isLivePagesAvailableInSpace &&
		!isLivePage &&
		(isLicensed || isGuest);

	const showIntroSpotlight =
		introSpotlightBaseCase &&
		!fg('cc_modernize_ga_changeboarding') &&
		fg('cc_onboarding_changeboarding_for_live_pages_eap');

	const showGAIntroSpotlight = introSpotlightBaseCase && fg('cc_modernize_ga_changeboarding');

	const showAiButtonTour =
		!isLivePage &&
		!isOnSpaceOverviewRoute &&
		isAiButtonVisible &&
		!hasDismissedAiButtonTour &&
		fg('confluence_ai_uber_button_changeboarding');

	const {
		isUserEnrolledInNav4Changeboarding,
		isUserCompletedNav4Changeboarding,
		loading: nav4Loading,
		error: nav4Error,
	} = useIsNav4Changeboarding();

	const completeIntroSpotlight = useCallback(() => {
		setIntroSpotlightDismissed(true);
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedIntroSpotlight,
			value: 'true',
		});
	}, [setOnboardingState]);

	const completeLiveDocTour = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedLiveDocTour,
			value: 'true',
		});
	}, [setOnboardingState]);

	const completeAiButtonTour = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedAiButtonTour,
			value: 'true',
		});
	}, [setOnboardingState]);

	// Skip onboarding for new users if features are enabled, by marking eligible onboarding as complete.
	// If we did not check to see if they were eligible for each onboarding before marking it complete,
	// we would risk marking onboarding complete when the feature is not yet enabled.
	useEffect(() => {
		if (fg('cc_modernize_ga_changeboarding')) {
			return;
		}
		if (
			error ||
			loading ||
			isEstablishedUserError ||
			isEstablishedUserLoading ||
			isAIFCOnbardingEligibleLoading ||
			isAIFCOnbardingEligibleError
		) {
			return;
		}
		if (!isEstablishedUser) {
			if (showIntroSpotlight) completeIntroSpotlight();
			if (showAiButtonTour) completeAiButtonTour();
		}
	}, [
		isEstablishedUser,
		showIntroSpotlight,
		showAiButtonTour,
		completeAiButtonTour,
		completeIntroSpotlight,
		completeLiveDocTour,
		error,
		isAIFCOnbardingEligibleLoading,
		isAIFCOnbardingEligibleError,
		isEstablishedUserError,
		isEstablishedUserLoading,
		loading,
	]);

	if (error || nav4Error || isAIFCOnbardingEligibleError) {
		return {
			...noChangeboarding,
			error: error || nav4Error || isAIFCOnbardingEligibleError,
		};
	}

	if (loading || nav4Loading || isEstablishedUserLoading || isAIFCOnbardingEligibleLoading) {
		return { ...noChangeboarding, loading: true };
	}

	if (showGAIntroSpotlight) {
		return {
			...noChangeboarding,
			introSpotlight: {
				isVisible: true,
				complete: completeIntroSpotlight,
			},
		};
	}

	// Do not start Live Doc changeboarding until eligible user has completed nav4 changeboarding
	if (isUserEnrolledInNav4Changeboarding && !isUserCompletedNav4Changeboarding) {
		return noChangeboarding;
	}

	if (!isEstablishedUser) {
		return noChangeboarding;
	}

	if (showAiButtonTour) {
		return {
			...noChangeboarding,
			aiButtonTour: {
				isVisible: true,
				complete: completeAiButtonTour,
			},
		};
	}

	if (showIntroSpotlight) {
		return {
			...noChangeboarding,
			introSpotlight: {
				isVisible: true,
				complete: completeIntroSpotlight,
			},
		};
	}

	return noChangeboarding;
};
